/* eslint-disable jsx-a11y/media-has-caption */
import { useEffect } from 'react'

import { NetworkStatus } from '@apollo/client'
import { ActionIcon, Divider, Loader, Text, Tooltip } from '@mantine/core'
import dayjs from 'dayjs'
import { Copy, ExternalLink } from 'lucide-react'
import { useInView } from 'react-intersection-observer'

import { Link, routes } from '@redwoodjs/router'
import { useQuery } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import AdminContainer from 'src/components/AdminContainer/AdminContainer'
import Tag from 'src/components/DataDisplay/Tag/Tag'
import Empty from 'src/components/Feedback/Empty/Empty'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { CallStatus } from 'src/context/PhoneDeviceContext'
import { DOWNLOAD_RECORDING_URL } from 'src/graphql/activityLogs.graphql'
import { GET_CALL_TRANSCRIPTS } from 'src/graphql/callTranscripts.graphql'
import { CallDirection } from 'src/graphql/types/activityLogs'
import { ApplicantCallConversionStatus } from 'src/graphql/types/callActivity'
import { buildFullName, formatSnakeValue } from 'src/lib/formatters'
import { formatInternationalPhoneNumber } from 'src/lib/phone.utils'
import CallConversionBadge from 'src/pages/CallAnalyticsPage/components/CallConversionBadge'
import { cn } from 'src/utils'

const Bubble = ({ transcript }) => {
  return (
    <div
      className={cn(
        'max-w-[90%] rounded p-2 text-doubleNickel-gray-700',
        (transcript.user === 'AGENT' ||
          transcript.user === 'SPEAKER_1' ||
          transcript.user === 'EMPLOYEE') &&
          'ml-auto bg-doubleNickel-brand-50',
        (transcript.user === 'HUMAN' ||
          transcript.user === 'SPEAKER_2' ||
          transcript.user === 'APPLICANT') &&
          'mr-auto bg-doubleNickel-gray-200'
      )}
    >
      {transcript.content}
    </div>
  )
}

const CallDetails = ({ activityLog, close }) => {
  const { currentUser } = useAuth()
  const isAdmin = currentUser && currentUser.roles.includes('ADMIN')
  const LIMIT = 20
  const page = 1
  const isLeftVoiceMail = activityLog.callStatus === CallStatus.LEFT_VOICEMAIL
  const isMachineDetected =
    activityLog?.machineDetectionResult?.startsWith('machine')
  const isFaxDetected = activityLog?.machineDetectionResult === 'fax'
  const voiceMailTitle = activityLog.voiceMailTemplate?.title
  const { ref, inView } = useInView({
    /* Optional options */
    threshold: 0,
  })

  const { data, fetchMore, networkStatus, loading } = useQuery(
    GET_CALL_TRANSCRIPTS,
    {
      notifyOnNetworkStatusChange: true,
      variables: {
        activityLogId: activityLog.activityLogId,
        pageInfo: { offset: (page - 1) * LIMIT, limit: LIMIT },
      },
      onError: () => {
        toast('Unable to get transcript file', 'error')
      },
    }
  )

  const transcriptData = data?.callTranscripts.items ?? []

  const { data: recordingUrl } = useQuery(DOWNLOAD_RECORDING_URL, {
    variables: {
      activityLogId: activityLog.activityLogId,
    },
    onError: () => {
      toast('Unable to get audio file', 'error')
    },
  })

  useEffect(() => {
    if (inView) {
      fetchMore({
        variables: {
          pageInfo: {
            offset: transcriptData.length ?? 0,
            limit: LIMIT,
          },
        },
      })
    }
  }, [inView])

  const isInbound = activityLog?.callDirection === CallDirection.INBOUND
  const fromNumber =
    formatInternationalPhoneNumber(activityLog?.from) ??
    (activityLog?.from ? `${activityLog.from} (Unsupported)` : 'Unknown')
  const toNumber =
    formatInternationalPhoneNumber(activityLog?.to) ??
    (activityLog?.to ? `${activityLog.to} (Unsupported)` : 'Unknown')
  const applicantName = activityLog?.applicant
    ? buildFullName(activityLog.applicant)
    : null
  const isVoECall = !!activityLog?.voeId
  const voiceAgentName = activityLog?.voiceAgent?.name
  const employeeName = activityLog?.employee
    ? buildFullName(activityLog.employee)
    : null
  const contactName = voiceAgentName || employeeName
  const callRecipientName = isVoECall
    ? activityLog?.voe?.companyName
    : applicantName
  const fromName = isInbound ? applicantName : contactName
  const toName = isInbound ? contactName : callRecipientName
  const booking = activityLog?.bookings?.[0] ?? null

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 overflow-hidden">
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto overscroll-contain">
        <div className="flex flex-row items-start justify-between text-sm font-medium">
          <div className="flex w-full flex-col gap-2">
            <div className="flex flex-row items-center gap-1">
              {isVoECall
                ? 'Employer Verification'
                : activityLog?.callDirection === CallDirection.INBOUND
                  ? 'Inbound'
                  : 'Outbound'}{' '}
              Call Details
              <Tooltip label={activityLog?.activityLogId} withArrow>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="xs"
                  onClick={() => {
                    navigator.clipboard.writeText(activityLog?.activityLogId)
                    toast('Activity Log ID copied to clipboard', 'success')
                  }}
                >
                  <Copy className="h-3 w-3 stroke-doubleNickel-gray-500" />
                </ActionIcon>
              </Tooltip>
              {activityLog?.blandCallSid && isAdmin && (
                <ActionIcon
                  component="a"
                  href={`https://app.bland.ai/dashboard/call-logs/${activityLog?.blandCallSid}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="subtle"
                  color="gray"
                  size="xs"
                >
                  <ExternalLink className="h-3 w-3 stroke-doubleNickel-gray-500" />
                </ActionIcon>
              )}
            </div>
            <div className="flex w-full flex-row items-center gap-1 overflow-auto text-xs">
              {isLeftVoiceMail && voiceMailTitle && (
                <div className="rounded bg-doubleNickel-error-100 p-2 py-1 text-doubleNickel-gray-800">
                  {voiceMailTitle}
                </div>
              )}
              {isMachineDetected && (
                <div className="text-nowrap rounded bg-doubleNickel-warning-100 px-2 py-1 text-doubleNickel-gray-800"> {/* pragma: allowlist secret */}
                  Answered by machine
                </div>
              )}
              {isFaxDetected && (
                <div className="text-nowrap rounded bg-doubleNickel-warning-100 px-2 py-1 text-doubleNickel-gray-800">
                  Fax Number
                </div>
              )}
              {activityLog?.connectedStatus && (
                <CallConversionBadge
                  status={ApplicantCallConversionStatus.CONNECTED}
                  connectedStatus={activityLog.connectedStatus}
                  voeFindings={activityLog.voeFindings}
                />
              )}
              {booking && booking?.employee && (
                <div
                  className={`text-nowrap rounded bg-doubleNickel-success-100 px-2 py-1 text-xs text-doubleNickel-success-700`}
                >
                  Booked with {buildFullName(booking.employee)}{' '}
                </div>
              )}
              {activityLog?.discussionTopics?.length > 0 &&
                activityLog?.discussionTopics?.map((topic) => (
                  <Tooltip
                    label={`Discussion Topics`}
                    key={topic}
                    withArrow
                    offset={8}
                  >
                    <div>
                      <Tag size="xs" showCloseButton={false}>
                        {topic === 'payment' ? 'compensation' : topic}
                      </Tag>
                    </div>
                  </Tooltip>
                ))}
              <AdminContainer>
                {activityLog?.voiceAgentAnalysisResults?.length > 0 &&
                  activityLog?.voiceAgentAnalysisResults.map((result) => (
                    <Tag
                      size="xs"
                      showCloseButton={false}
                      key={result.name}
                      variant="error"
                    >
                      {formatSnakeValue(result.name)}
                    </Tag>
                  ))}
              </AdminContainer>
            </div>
          </div>
        </div>
        <Divider variant="dashed" />
        <div className="space-y-4">
          <Text size="xs" fw={400} className="text-doubleNickel-gray-500">
            OVERVIEW
          </Text>
          {dayjs(activityLog.activityDate).isValid() && (
            <div className="flex flex-row items-center justify-between">
              <Text size="xs" fw={300}>
                Activity Date
              </Text>
              <Text size="xs" fw={300}>
                {dayjs(activityLog.activityDate).format('MM/DD/YYYY hh:mm A')}
              </Text>
            </div>
          )}
          <div className="flex flex-row items-center justify-between">
            <Text size="xs" fw={300}>
              From
            </Text>
            <Text size="xs" fw={300}>
              {fromNumber}
              {fromName && (
                <>
                  {' - '}
                  {isInbound && applicantName ? (
                    <Link
                      to={routes.applicantDetails({
                        id: activityLog.applicant.applicantId,
                        tab: 'application',
                      })}
                      className="text-doubleNickel-brand-500 hover:underline"
                    >
                      {fromName}
                    </Link>
                  ) : (
                    fromName
                  )}
                </>
              )}
            </Text>
          </div>
          <div className="flex flex-row items-center justify-between">
            <Text size="xs" fw={300}>
              To
            </Text>
            <Text size="xs" fw={300}>
              {toNumber}
              {toName && (
                <>
                  {' - '}
                  {!isInbound && !isVoECall && applicantName ? (
                    <Link
                      to={routes.applicantDetails({
                        id: activityLog.applicant.applicantId,
                        tab: 'application',
                      })}
                      className="text-doubleNickel-brand-500 hover:underline"
                    >
                      {toName}
                    </Link>
                  ) : (
                    toName
                  )}
                </>
              )}
            </Text>
          </div>
          {isVoECall && applicantName && (
            <div className="flex flex-row items-center justify-between">
              <Text size="xs" fw={300}>
                On behalf of
              </Text>
              <Text size="xs" fw={300}>
                <Link
                  to={routes.applicantDetails({
                    id: activityLog.applicant.applicantId,
                    tab: 'application',
                  })}
                  className="text-doubleNickel-brand-500 hover:underline"
                >
                  {applicantName}
                </Link>
              </Text>
            </div>
          )}
          {isVoECall && activityLog?.applicant?.applicantId && (
            <div className="flex flex-row items-center justify-between">
              <Text size="xs" fw={300}>
                VoE record
              </Text>
              <Text size="xs" fw={300}>
                <Link
                  to={routes.applicantDetails({
                    id: activityLog.applicant.applicantId,
                    tab: 'voe',
                    voeId: activityLog.voeId,
                  })}
                  onClick={() => close?.()}
                  className="text-doubleNickel-brand-500 hover:underline"
                >
                  {activityLog.voe?.companyName ?? 'Open VoE'}
                </Link>
              </Text>
            </div>
          )}
        </div>
        {activityLog?.transcriptSummary &&
          activityLog?.transcriptSummary !== 'NO_ANSWER' && (
            <>
              <Divider variant="dashed" />
              <div className="space-y-2">
                <Text size="xs" fw={400} className="text-doubleNickel-gray-500">
                  SUMMARY
                </Text>

                <Text size="xs" fw={300}>
                  {activityLog?.transcriptSummary}
                </Text>
              </div>
            </>
          )}
        <Divider variant="dashed" />
        <div className="flex flex-col gap-3 text-sm">
          <Text size="xs" fw={400} className="text-doubleNickel-gray-500">
            TRANSCRIPT
          </Text>
          {transcriptData?.length === 0 && !loading ? (
            <Empty
              title={
                activityLog?.transcriptFileUrl
                  ? 'We are unable to display this transcript'
                  : 'No transcript available'
              }
              subtitle={
                activityLog?.transcriptFileUrl
                  ? 'You can download it for viewing'
                  : isFaxDetected
                    ? 'The number is a fax machine — no transcript available'
                    : isMachineDetected
                      ? 'The call was answered by an automated system — no transcript available'
                      : activityLog?.callStatus === CallStatus.LEFT_VOICEMAIL
                        ? 'A voicemail was left — no transcript available'
                        : activityLog?.callStatus === CallStatus.NO_ANSWER
                          ? 'The call was not answered — no transcript available'
                          : activityLog?.callStatus === CallStatus.BUSY
                            ? 'The line was busy — no transcript available'
                            : activityLog?.callStatus === CallStatus.FAILED
                              ? 'The call failed to connect — no transcript available'
                              : 'Please try another call'
              }
            />
          ) : (
            <div className="flex flex-col gap-4">
              {transcriptData.map((transcript) => (
                <Bubble
                  key={transcript.callTranscriptId}
                  transcript={transcript}
                />
              ))}
              {networkStatus === NetworkStatus.fetchMore && (
                <Loader size="sm" className="my-4 flex w-full justify-center" />
              )}
              {transcriptData.length < data?.callTranscripts.totalCount &&
                !loading && (
                  <div ref={ref} className="opacity-0">
                    Inview placeholder
                  </div>
                )}
            </div>
          )}
        </div>
      </div>

      {recordingUrl?.getRecordingDownloadUrl?.url && (
        <div className="flex shrink-0 items-center justify-center">
          <audio
            src={recordingUrl?.getRecordingDownloadUrl?.url || ''}
            controls
            className="h-10 w-full rounded bg-[#F1F3F4]"
          >
            Your browser does not support the audio element.
          </audio>
        </div>
      )}
    </div>
  )
}

export default CallDetails

